import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { 
  Mail, 
  MailOpen,
  Trash2, 
  Search,
  Phone,
  Calendar,
  User,
  MessageSquare,
  CheckCircle,
  Clock,
  Filter,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AdminContacts = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, unread: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all'); // all, unread, read
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    fetchContacts();
  }, [filter]);

  const fetchContacts = async () => {
    try {
      const statusParam = filter === 'all' ? '' : `?status=${filter === 'read' ? 'read' : 'new'}`;
      const response = await authFetch(`${API_URL}/api/admin/contacts${statusParam}`, {
        
      });
      
      if (response.ok) {
        const data = await response.json();
        setContacts(data.contacts || []);
        setStats({ total: data.total, unread: data.unread });
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
      toast.error('Error carregant missatges');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (contactId) => {
    try {
      const response = await authFetch(`${API_URL}/api/admin/contacts/${contactId}/status?status=read`, {
        method: 'PUT',
        
      });
      
      if (response.ok) {
        setContacts(prev => prev.map(c => 
          c.id === contactId ? { ...c, status: 'read' } : c
        ));
        setStats(prev => ({ ...prev, unread: Math.max(0, prev.unread - 1) }));
        toast.success('Marcat com a llegit');
      }
    } catch (error) {
      toast.error('Error actualitzant estat');
    }
  };

  const deleteContact = async (contactId) => {
    if (!window.confirm('Segur que vols eliminar aquest missatge?')) return;
    
    try {
      const response = await authFetch(`${API_URL}/api/admin/contacts/${contactId}`, {
        method: 'DELETE',
        
      });
      
      if (response.ok) {
        setContacts(prev => prev.filter(c => c.id !== contactId));
        setStats(prev => ({ ...prev, total: prev.total - 1 }));
        toast.success('Missatge eliminat');
      }
    } catch (error) {
      toast.error('Error eliminant missatge');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `Fa ${diffMins} min`;
    if (diffHours < 24) return `Fa ${diffHours}h`;
    if (diffDays < 7) return `Fa ${diffDays} dies`;
    
    return date.toLocaleDateString('ca-ES', { 
      day: '2-digit', 
      month: 'short',
      year: diffDays > 365 ? 'numeric' : undefined
    });
  };

  const filteredContacts = contacts.filter(contact => {
    const matchesSearch = 
      contact.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.message?.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesSearch;
  });

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-[#1A4D2E]" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-display font-semibold text-[#1A4D2E] flex items-center gap-2">
              <Mail className="w-6 h-6" />
              Missatges de Contacte
            </h1>
            <p className="text-[#666666] mt-1">
              Gestiona els missatges rebuts del formulari de contacte
            </p>
          </div>
          
          <Button
            variant="outline"
            onClick={fetchContacts}
            className="gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Actualitzar
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white border border-[#E5E5E5] rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#1A4D2E]/10 flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-[#1A4D2E]" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#333333]">{stats.total}</p>
                <p className="text-sm text-[#666666]">Total missatges</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white border border-[#E5E5E5] rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                stats.unread > 0 ? 'bg-amber-100' : 'bg-green-100'
              }`}>
                {stats.unread > 0 ? (
                  <Mail className="w-5 h-5 text-amber-600" />
                ) : (
                  <CheckCircle className="w-5 h-5 text-green-600" />
                )}
              </div>
              <div>
                <p className="text-2xl font-bold text-[#333333]">{stats.unread}</p>
                <p className="text-sm text-[#666666]">Sense llegir</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#999999]" />
            <Input
              placeholder="Cercar per nom, email, assumpte..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          
          <div className="flex gap-2">
            <Button
              variant={filter === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter('all')}
              className={filter === 'all' ? 'bg-[#1A4D2E] hover:bg-[#143d24]' : ''}
            >
              Tots
            </Button>
            <Button
              variant={filter === 'unread' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter('unread')}
              className={filter === 'unread' ? 'bg-[#1A4D2E] hover:bg-[#143d24]' : ''}
            >
              <Mail className="w-3 h-3 mr-1" />
              Nous
            </Button>
            <Button
              variant={filter === 'read' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter('read')}
              className={filter === 'read' ? 'bg-[#1A4D2E] hover:bg-[#143d24]' : ''}
            >
              <MailOpen className="w-3 h-3 mr-1" />
              Llegits
            </Button>
          </div>
        </div>

        {/* Contacts List */}
        <div className="space-y-3">
          {filteredContacts.length === 0 ? (
            <div className="bg-white border border-[#E5E5E5] rounded-lg p-12 text-center">
              <Mail className="w-12 h-12 text-[#CCCCCC] mx-auto mb-4" />
              <p className="text-[#666666]">
                {searchTerm ? 'No s\'han trobat missatges' : 'No hi ha missatges'}
              </p>
            </div>
          ) : (
            filteredContacts.map((contact) => (
              <div 
                key={contact.id}
                className={`bg-white border rounded-lg overflow-hidden transition-all ${
                  contact.status !== 'read' 
                    ? 'border-amber-300 bg-amber-50/30' 
                    : 'border-[#E5E5E5]'
                }`}
              >
                {/* Header row */}
                <div 
                  className="p-4 cursor-pointer hover:bg-[#FAFAFA] transition-colors"
                  onClick={() => {
                    setExpandedId(expandedId === contact.id ? null : contact.id);
                    if (contact.status !== 'read') {
                      markAsRead(contact.id);
                    }
                  }}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      {/* Status icon */}
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        contact.status !== 'read' ? 'bg-amber-100' : 'bg-[#F5F5F5]'
                      }`}>
                        {contact.status !== 'read' ? (
                          <Mail className="w-4 h-4 text-amber-600" />
                        ) : (
                          <MailOpen className="w-4 h-4 text-[#999999]" />
                        )}
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`font-medium ${
                            contact.status !== 'read' ? 'text-[#333333]' : 'text-[#666666]'
                          }`}>
                            {contact.name}
                          </span>
                          <span className="text-sm text-[#999999]">
                            &lt;{contact.email}&gt;
                          </span>
                        </div>
                        
                        <p className={`text-sm mt-1 ${
                          contact.status !== 'read' ? 'font-medium text-[#333333]' : 'text-[#666666]'
                        }`}>
                          {contact.subject || '(Sense assumpte)'}
                        </p>
                        
                        {expandedId !== contact.id && (
                          <p className="text-sm text-[#999999] truncate mt-1">
                            {contact.message?.substring(0, 100)}...
                          </p>
                        )}
                      </div>
                    </div>
                    
                    {/* Right side */}
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className="text-xs text-[#999999] flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatDate(contact.created_at)}
                      </span>
                      {expandedId === contact.id ? (
                        <ChevronUp className="w-4 h-4 text-[#999999]" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-[#999999]" />
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Expanded content */}
                {expandedId === contact.id && (
                  <div className="border-t border-[#E5E5E5] p-4 bg-[#FAFAFA]">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div className="flex items-center gap-2 text-sm">
                        <User className="w-4 h-4 text-[#999999]" />
                        <span className="text-[#666666]">Nom:</span>
                        <span className="font-medium">{contact.name}</span>
                      </div>
                      
                      <div className="flex items-center gap-2 text-sm">
                        <Mail className="w-4 h-4 text-[#999999]" />
                        <span className="text-[#666666]">Email:</span>
                        <a 
                          href={`mailto:${contact.email}`}
                          className="font-medium text-[#1A4D2E] hover:underline"
                        >
                          {contact.email}
                        </a>
                      </div>
                      
                      {contact.phone && (
                        <div className="flex items-center gap-2 text-sm">
                          <Phone className="w-4 h-4 text-[#999999]" />
                          <span className="text-[#666666]">Telèfon:</span>
                          <a 
                            href={`tel:${contact.phone}`}
                            className="font-medium text-[#1A4D2E] hover:underline"
                          >
                            {contact.phone}
                          </a>
                        </div>
                      )}
                      
                      <div className="flex items-center gap-2 text-sm">
                        <Calendar className="w-4 h-4 text-[#999999]" />
                        <span className="text-[#666666]">Data:</span>
                        <span className="font-medium">
                          {new Date(contact.created_at).toLocaleString('ca-ES')}
                        </span>
                      </div>
                    </div>
                    
                    {/* Message */}
                    <div className="bg-white border border-[#E5E5E5] rounded-lg p-4 mb-4">
                      <p className="text-sm font-medium text-[#666666] mb-2">Missatge:</p>
                      <p className="text-[#333333] whitespace-pre-wrap">{contact.message}</p>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.location.href = `mailto:${contact.email}?subject=Re: ${contact.subject || 'Consulta'}`}
                        className="gap-2"
                      >
                        <ExternalLink className="w-3 h-3" />
                        Respondre per email
                      </Button>
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => deleteContact(contact.id)}
                        className="gap-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-3 h-3" />
                        Eliminar
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminContacts;
